// Cost "overview + per-user" screen. Headline KPI cards from `summary` plus a
// paged, backend-sorted table of users by total spend. Row click opens the
// per-session drill-down. Reused for the global view and, scoped by
// `corporateId`, for a single enterprise.

import React, { useCallback, useEffect, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import {
  getEnterpriseCostReport,
  getUserCostReport,
} from "../../../Services/adminReporting";
import { Card, CardContent } from "../../../ui/card";
import { Skeleton } from "../../../ui/skeleton";
import { cn } from "../../../lib/utils";
import { CARD_CLASS, Panel } from "../../../Components/admin/reporting/controls";
import UserCostDrawer from "./UserCostDrawer";
import {
  ProvisionalFlags,
  TokenBreakdown,
  fmtMinutes,
  fmtNum,
  fmtUsd,
} from "./costFormat";

const PAGE_SIZE = 100;

const Stat = ({ label, primary, sub, accent }) => (
  <Card className={cn(CARD_CLASS, accent && "border-sky-300 bg-sky-50/60")}>
    <CardContent className="p-4">
      <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div
        className={cn(
          "mt-1 text-2xl font-semibold tabular-nums",
          accent && "text-sky-600"
        )}
      >
        {primary ?? "—"}
      </div>
      {sub ? <div className="mt-0.5 text-xs text-muted-foreground">{sub}</div> : null}
    </CardContent>
  </Card>
);

const CostSpendTab = ({ windowDays, range, corporateId = null }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [offset, setOffset] = useState(0);
  const [activeUser, setActiveUser] = useState(null);
  const [expanded, setExpanded] = useState(() => new Set()); // user_ids with tokens shown

  const toggleExpanded = (userId) =>
    setExpanded((cur) => {
      const next = new Set(cur);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });

  // Reset paging whenever the scope or window changes.
  useEffect(() => {
    setOffset(0);
    setExpanded(new Set());
  }, [windowDays, range, corporateId]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = corporateId
        ? await getEnterpriseCostReport(corporateId, windowDays, range, {
            limit: PAGE_SIZE,
            offset,
          })
        : await getUserCostReport(windowDays, range, {
            limit: PAGE_SIZE,
            offset,
          });
      setData(payload);
    } catch (err) {
      setError(err?.response?.data?.message || "Failed to load cost report.");
    } finally {
      setLoading(false);
    }
  }, [windowDays, range, corporateId, offset]);

  useEffect(() => {
    load();
  }, [load]);

  const summary = data?.summary || {};
  const users = data?.users || [];
  const totalUsers = summary.users ?? 0;
  const pageStart = totalUsers ? offset + 1 : 0;
  const pageEnd = offset + (data?.paging?.returned ?? users.length);
  const hasNext = pageEnd < totalUsers;

  const voiceShare = summary.total_cost_usd
    ? Math.round(((summary.voice_cost_usd || 0) / summary.total_cost_usd) * 100)
    : null;

  if (loading && !data) {
    return (
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-28 w-full" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-destructive/40 bg-destructive/5 px-4 py-3 text-sm text-destructive">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* KPI cards — reflect the full filtered set, not just this page. */}
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        <Stat
          label="Total spend"
          primary={fmtUsd(summary.total_cost_usd)}
          sub={`${fmtNum(totalUsers)} users`}
          accent
        />
        <Stat
          label="Voice spend"
          primary={fmtUsd(summary.voice_cost_usd)}
          sub={voiceShare != null ? `${voiceShare}% of total` : "realtime model"}
        />
        <Stat
          label="Chat spend"
          primary={fmtUsd(summary.chat_cost_usd)}
          sub={voiceShare != null ? `${100 - voiceShare}% of total` : "text only"}
        />
        <Stat
          label="Voice minutes"
          primary={fmtMinutes(summary.voice_minutes)}
          sub="billed audio time"
        />
      </div>

      <Panel
        title="Spend by user"
        right={
          <span className="text-xs text-muted-foreground">
            {totalUsers
              ? `${pageStart}–${pageEnd} of ${fmtNum(totalUsers)} · sorted by total`
              : "no spend in window"}
          </span>
        }
      >
        {users.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No usage in this window.
          </p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs text-muted-foreground">
                    <th className="w-6 py-2 pr-1" />
                    <th className="py-2 pr-3 font-medium">User</th>
                    {!corporateId ? (
                      <th className="py-2 px-3 font-medium">Org</th>
                    ) : null}
                    <th className="py-2 px-3 text-right font-medium">Total</th>
                    <th className="py-2 px-3 text-right font-medium">Voice</th>
                    <th className="py-2 px-3 text-right font-medium">Chat</th>
                    <th className="py-2 px-3 text-right font-medium">Sessions</th>
                    <th className="py-2 px-3 text-right font-medium">Voice min</th>
                    <th className="py-2 px-3 text-right font-medium">$/min</th>
                    <th className="py-2 pl-3 font-medium">Flags</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => {
                    const isOpen = expanded.has(u.user_id);
                    return (
                      <React.Fragment key={u.user_id}>
                        <tr
                          onClick={() => setActiveUser(u.user_id)}
                          className="cursor-pointer border-b transition-colors hover:bg-slate-50"
                        >
                          <td className="py-2 pr-1 align-top">
                            <button
                              type="button"
                              aria-label={isOpen ? "Hide tokens" : "Show tokens"}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(u.user_id);
                              }}
                              className="rounded p-0.5 text-muted-foreground hover:bg-slate-200 hover:text-foreground"
                            >
                              {isOpen ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                          </td>
                          <td className="py-2 pr-3 align-top">
                            <div className="font-medium">{u.name || "—"}</div>
                            <div className="text-[11px] text-muted-foreground">
                              {u.email || u.phone || (
                                <span className="font-mono">{u.user_id}</span>
                              )}
                            </div>
                          </td>
                          {!corporateId ? (
                            <td className="py-2 px-3 align-top font-mono text-xs text-muted-foreground">
                              {u.corporate_id || "—"}
                            </td>
                          ) : null}
                          <td className="py-2 px-3 text-right align-top font-semibold tabular-nums">
                            {fmtUsd(u.total_cost_usd)}
                          </td>
                          <td className="py-2 px-3 text-right align-top tabular-nums text-muted-foreground">
                            {fmtUsd(u.voice_cost_usd)}
                          </td>
                          <td className="py-2 px-3 text-right align-top tabular-nums text-muted-foreground">
                            {fmtUsd(u.chat_cost_usd)}
                          </td>
                          <td className="py-2 px-3 text-right align-top tabular-nums">
                            {fmtNum(u.sessions)}
                          </td>
                          <td className="py-2 px-3 text-right align-top tabular-nums">
                            {fmtMinutes(u.voice_minutes)}
                          </td>
                          <td className="py-2 px-3 text-right align-top tabular-nums">
                            {u.voice_cost_per_minute == null
                              ? "—"
                              : fmtUsd(u.voice_cost_per_minute, true)}
                          </td>
                          <td className="py-2 pl-3 align-top">
                            <ProvisionalFlags
                              pricingUnverified={u.pricing_unverified}
                              missingRate={u.missing_rate}
                            />
                          </td>
                        </tr>
                        {isOpen ? (
                          <tr className="border-b bg-slate-50/60">
                            <td />
                            <td
                              colSpan={corporateId ? 8 : 9}
                              className="py-3 pr-3"
                            >
                              <div className="grid gap-4 sm:grid-cols-2">
                                <TokenBreakdown
                                  tokens={u.voice_tokens}
                                  label="Voice tokens"
                                />
                                <TokenBreakdown
                                  tokens={u.chat_tokens}
                                  label="Chat tokens"
                                />
                              </div>
                            </td>
                          </tr>
                        ) : null}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {(offset > 0 || hasNext) && (
              <div className="mt-4 flex items-center justify-between border-t pt-3 text-xs">
                <button
                  type="button"
                  disabled={offset === 0 || loading}
                  onClick={() => setOffset((o) => Math.max(0, o - PAGE_SIZE))}
                  className="rounded-md border px-3 py-1 font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                >
                  ← Prev
                </button>
                <span className="text-muted-foreground">
                  Page {Math.floor(offset / PAGE_SIZE) + 1}
                </span>
                <button
                  type="button"
                  disabled={!hasNext || loading}
                  onClick={() => setOffset((o) => o + PAGE_SIZE)}
                  className="rounded-md border px-3 py-1 font-medium text-muted-foreground transition-colors hover:text-foreground disabled:opacity-40"
                >
                  Next →
                </button>
              </div>
            )}
          </>
        )}
      </Panel>

      <UserCostDrawer
        userId={activeUser}
        windowDays={windowDays}
        range={range}
        onClose={() => setActiveUser(null)}
      />
    </div>
  );
};

export default CostSpendTab;
